import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import { LeaveDto, LeaveStatus } from '../types/leaves.types';
import { User } from '../types/user.types';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { logoDark} from './logoBase64';
import leaveService from '../services/leave/leave.service';

export const normalizeTurkishChars = (text: string): string => {
  if (!text) return '';
  const turkishMap: { [key: string]: string } = {
    'İ': 'I', 'ı': 'i',
    'Ö': 'O', 'ö': 'o',
    'Ü': 'U', 'ü': 'u',
    'Ş': 'S', 'ş': 's',
    'Ç': 'C', 'ç': 'c',
    'Ğ': 'G', 'ğ': 'g',
  };
  return text.replace(/[İıÖöÜüŞşÇçĞğ]/g, (match) => turkishMap[match] || match);
};

// Yardımcı formatlama fonksiyonları
const formatLeaveType = (type: string): string => {
  switch (type) {
    case 'sick': return 'Hastalik Izni';
    case 'annual': return 'Yillik Izni';
    case 'unpaid': return 'Ucretsiz Izni';
    case 'special': return 'Ozel Izni';
    case 'hourly': return 'Saatlik Izni';
    case 'medical': return 'Saglik Izni';
    case 'marriage': return 'Evlilik Izni';
    case 'birth': return 'Dogum Izni';
    case 'death': return 'Olum Izni';
    default: return type;
  }
};

const formatLeaveStatus = (status: string): string => {
  switch (status) {
    case 'pending': return 'Beklemede';
    case 'approved': return 'Onaylandi';
    case 'rejected': return 'Reddedildi';
    default: return status;
  }
};

// Leave exports
export const handleExportExcelLeave = (leaves: LeaveDto[]) => {
  // İzin türü çevirileri
  const leaveTypeTranslations: { [key: string]: string } = {
    sick: "Hastalık İzni",
    annual: "Yıllık İzin",
    unpaid: "Ücretsiz İzin",
    special: "Özel İzin",
    hourly: "Saatlik İzin",
    medical: "Sağlık İzni",
    marriage: "Evlilik İzni",
    birth: "Doğum İzni",
    death: "Ölüm İzni",
    paternity: "Babalık İzni",
    casual: "Günübirlik İzin",
    personal: "Kişisel İzin",
    educational: "Eğitim İzni",
  };

  const getLeaveTypeText = (type: string): string => {
    return leaveTypeTranslations[type] || type;
  };

  // Durum gösterimi
  const getStatusText = (leave: LeaveDto): string => {
    // Hiyerarşik İzin durumu
    if (leave.approvalFlow && leave.approvalFlow.steps.length > 0) {
      const steps = leave.approvalFlow.steps;
      const allApproved = steps.every(s => s.status === 'approved');
      if (allApproved) return "Tüm onaylar tamamlandı";

      for (const step of steps) {
        const approverName = `${step.approver.firstName} ${step.approver.lastName}`;
        if (step.status === 'rejected') return `${approverName} tarafından reddedildi`;
        if (step.status === 'pending_approval' || step.status === 'pending') {
          return `${approverName} onayı bekleniyor (${step.stepOrder}/${steps.length})`;
        }
      }
    }

    // Hiyerarşik olmayan veya genel durum
    if (leave.status === LeaveStatus.APPROVED) {
      const processor = leave.processedBy || leave.approvedBy;
      return processor ? `${processor.firstName} ${processor.lastName} tarafından onaylandı` : "Onaylandı";
    }

    if (leave.status === LeaveStatus.REJECTED) {
      const processor = leave.processedBy || leave.rejectedBy;
      return processor ? `${processor.firstName} ${processor.lastName} tarafından reddedildi` : "Reddedildi";
    }
    
    if (leave.status === LeaveStatus.PENDING_HIERARCHY_APPROVAL) return "Onay Bekliyor";
    if (leave.status === LeaveStatus.PENDING) return "Beklemede";

    return leave.status;
  };

  // Excel için veri hazırlama
  const excelData = leaves.map((leave) => {
    return {
      'Kullanıcı': leave.user ? `${leave.user.firstName} ${leave.user.lastName}` : 'Belirsiz',
      'Departman': leave.user?.department?.map(d => d.name).join(', ') || 'Belirsiz',
      'Şube': leave.user?.branch?.map(b => b.name).join(', ') || 'Belirsiz',
      'İzin Türü': getLeaveTypeText(leave.type),
      'Tarih Aralığı': `${dayjs(leave.startDate).format('DD.MM.YYYY')} - ${dayjs(leave.endDate).format('DD.MM.YYYY')}`,
      'Saat': leave.startTime && leave.endTime ? `${leave.startTime} - ${leave.endTime}` : '-',
      'Durum': getStatusText(leave),
      'Açıklama': leave.reason ? leave.reason : '-',
      'Oluşturulma': leave.createdAt ? dayjs(leave.createdAt).format('DD.MM.YYYY HH:mm') : '-',
    };
  });

  // Excel workbook oluşturma
  const workbook = XLSX.utils.book_new();
  
  // Kitap özellikleri ayarlama
  workbook.Props = {
    Title: 'İzin Raporu',
    Subject: 'İzin Verileri',
    Author: 'DINOX Sistem',
    CreatedDate: new Date()
  };

  // Workshet oluşturma ve ekleme
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Sütun genişliklerini ayarlama
  const wscols = [
    { wch: 20 }, // Kullanıcı
    { wch: 20 }, // Departman
    { wch: 15 }, // Şube
    { wch: 20 }, // İzin Türü
    { wch: 30 }, // Tarih Aralığı
    { wch: 20 }, // Saat
    { wch: 50 }, // Durum (genişletildi)
    { wch: 40 }, // Açıklama
    { wch: 20 }, // Oluşturulma
  ];
  
  worksheet['!cols'] = wscols;
  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'İzin Verileri');
  
  // Excel dosyasını kaydetme
  const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
  XLSX.writeFile(workbook, `Izin_Raporu_${currentDate}.xlsx`);
};

export const handleExportPdfLeave = async (leaves: LeaveDto[]) => {
  // A4 boyutunda yatay (landscape) PDF oluştur
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  // Türkçe karakter desteği için Roboto fontunu yükle
  let pdfFont = 'helvetica';
  try {
    const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
    const customFont = await getFontAsBase64(fontUrl);
    doc.addFileToVFS('Roboto-Regular.ttf', customFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
    pdfFont = 'Roboto';
  } catch (error) {
    console.warn('handleExportPdfLeave: Roboto font yüklenemedi, varsayılan font kullanılıyor.', error);
  }
  // Roboto yüklendiyse ham Türkçe metni kullan, yüklenemediyse ASCII'ye normalize et.
  const t = (text: string): string => (pdfFont === 'Roboto' ? text : normalizeTurkishChars(text));

  // PDF başlık ve metadata ayarları - Türkçe karakter desteği
  doc.setLanguage("tr");
  doc.setProperties({
    title: t("İzin Listesi Raporu"),
    subject: t("İzin Yönetim Sistemi"),
    author: "DINOX HR",
    keywords: t("izin, rapor, insan kaynakları"),
    creator: "DINOX HR Sistemi"
  });

  // ===== HEADER (ÜST BİLGİ) =====
  // Modern gradient header arka planı
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  
  // Gradient header arka planı
  const gradientColors = {
    start: [41, 128, 185], // Mavi başlangıç rengi (#2980b9)
    end: [52, 152, 219]    // Daha açık mavi bitiş rengi (#3498db)
  };
  
  // Header arka plan gradient
  for (let i = 0; i < 25; i++) {
    const factor = i / 25;
    const r = Math.floor(gradientColors.start[0] + (gradientColors.end[0] - gradientColors.start[0]) * factor);
    const g = Math.floor(gradientColors.start[1] + (gradientColors.end[1] - gradientColors.start[1]) * factor);
    const b = Math.floor(gradientColors.start[2] + (gradientColors.end[2] - gradientColors.start[2]) * factor);
    
    doc.setFillColor(r, g, b);
    doc.rect(0, i, width, 1, 'F');
  }
  
  // Logo bölümü için koyu arka plan
  doc.setFillColor(25, 80, 120); // Koyu mavi
  doc.roundedRect(10, 5, 45, 15, 3, 3, 'F');
  
  // Logo yerine şirket adı (logo eklenebilir)
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text("Dinox HR", 18, 15);

  // Rapor başlığı
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.text(t("İzin Listesi Raporu"), 70, 15);

  // Tarih bilgisi
  doc.setFont(pdfFont, "normal");
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  const dateText = `${dayjs().format('DD MMMM YYYY')}`;
  doc.text(dateText, width - 15 - doc.getTextWidth(dateText), 15);
  
  // Header ve içerik arasına ayırıcı çizgi
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.5);
  // doc.line(10, 25, width - 10, 25);
  
  // ===== İSTATİSTİK BÖLÜMÜ =====
  // İstatistik kutusu
  // doc.setFillColor(245, 245, 245); // Açık gri
  //doc.roundedRect(10, 30, width - 20, 30, 2, 2, 'F');
  
  // // İstatistik başlığı
  // doc.setFont("helvetica", "bold");
  // doc.setFontSize(12);
  // doc.setTextColor(80, 80, 80);
  // doc.text("IZIN ISTATISTIKLERI", 20, 40);
  
  // İstatistik verileri
  const pendingCount = leaves.filter(leave => leave.status === 'pending').length;
  const approvedCount = leaves.filter(leave => leave.status === 'approved').length;
  const rejectedCount = leaves.filter(leave => leave.status === 'rejected').length;
  
  // İstatistik kutuları
  const boxWidth = 50;
  const boxMargin = 10;
  const startX = 10; // Soldan başlayacak şekilde
  const startY = 43; // İstatistik başlığının altına
  
  // Toplam izin kutusu
  doc.setFillColor(52, 152, 219); // Mavi
  doc.roundedRect(startX, startY, boxWidth, 14, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(12);
  doc.text(t(`Toplam: ${leaves.length}`), startX + 10, startY + 9);

  // Bekleyen izin kutusu
  doc.setFillColor(243, 156, 18); // Turuncu
  doc.roundedRect(startX + boxWidth + boxMargin, startY, boxWidth, 14, 2, 2, 'F');
  doc.text(t(`Bekleyen: ${pendingCount}`), startX + boxWidth + boxMargin + 10, startY + 9);

  // Onaylanan izin kutusu
  doc.setFillColor(46, 204, 113); // Yeşil
  doc.roundedRect(startX + 2 * (boxWidth + boxMargin), startY, boxWidth, 14, 2, 2, 'F');
  doc.text(t(`Onaylanan: ${approvedCount}`), startX + 2 * (boxWidth + boxMargin) + 5, startY + 9);

  // Reddedilen izin kutusu
  doc.setFillColor(231, 76, 60); // Kırmızı
  doc.roundedRect(startX + 3 * (boxWidth + boxMargin), startY, boxWidth, 14, 2, 2, 'F');
  doc.text(t(`Reddedilen: ${rejectedCount}`), startX + 3 * (boxWidth + boxMargin) + 5, startY + 9);

  // ===== TABLO BÖLÜMÜ =====
  // Tablo sütun başlıkları - Türkçe karakter desteği ile
  const tableColumn = [
    ['Kullanıcı', 'Departman', 'İzin Türü', 'Başlangıç', 'Bitiş', 'Saat', 'Durum', 'Açıklama'].map(t)
  ];

  // Tablo satırları
  const tableRows = leaves.map(leave => [
    t(leave.user ? `${leave.user.firstName} ${leave.user.lastName}` : '-'),
    t(leave.user?.department?.map(d => d.name).join(', ') || '-'),
    t(formatLeaveType(leave.type)),
    dayjs(leave.startDate).format('DD.MM.YYYY'),
    dayjs(leave.endDate).format('DD.MM.YYYY'),
    leave.startTime && leave.endTime ? `${leave.startTime} - ${leave.endTime}` : '-',
    t(formatLeaveStatus(leave.status)),
    t(leave.reason ? (leave.reason.length > 40 ? leave.reason.substring(0, 40) + '...' : leave.reason) : '-')
  ]);

  // Gelişmiş tablo oluşturma
  autoTable(doc, {
    head: tableColumn,
    body: tableRows,
    startY: 60, // İstatistikler sonrası başlangıç (Y değeri artırıldı)
    theme: 'grid', // Izgaralı tema
    styles: {
      font: pdfFont,
      fontSize: 10,
      cellPadding: 6,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      textColor: [50, 50, 50],
      valign: 'middle'
    },
    headStyles: {
      fillColor: [41, 128, 185], // Mavi başlık
      textColor: 255,
      fontSize: 11,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      minCellHeight: 14
    },
    columnStyles: {
      0: { cellWidth: 35 },  // Kullanıcı
      1: { cellWidth: 28 },  // Departman
      2: { cellWidth: 30 },  // İzin Türü
      3: { cellWidth: 22, halign: 'center' }, // Başlangıç
      4: { cellWidth: 22, halign: 'center' }, // Bitiş
      5: { cellWidth: 25, halign: 'center' }, // Saat
      6: { cellWidth: 25, halign: 'center' }, // Durum
      7: { cellWidth: 50 }   // Açıklama
    },
    alternateRowStyles: {
      fillColor: [245, 250, 254] // Açık mavi alternatif satır
    },
    // Her sayfada header ve footer
    didDrawPage: (data) => {
      // Sayfa bilgisi
      const pageCount = doc.getNumberOfPages();
      const currentPage = data.pageNumber;
      
      // Sonraki sayfalarda (ilk sayfa hariç) header tekrarı
      if (currentPage > 1) {
        // Header arka plan gradient
        for (let i = 0; i < 25; i++) {
          const factor = i / 25;
          const r = Math.floor(gradientColors.start[0] + (gradientColors.end[0] - gradientColors.start[0]) * factor);
          const g = Math.floor(gradientColors.start[1] + (gradientColors.end[1] - gradientColors.start[1]) * factor);
          const b = Math.floor(gradientColors.start[2] + (gradientColors.end[2] - gradientColors.start[2]) * factor);
          
          doc.setFillColor(r, g, b);
          doc.rect(0, i, width, 1, 'F');
        }
        
        // Logo bölümü
        doc.setFillColor(25, 80, 120);
        doc.roundedRect(10, 5, 45, 15, 3, 3, 'F');
        
        // Logo metni
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.text("DINOX HR", 18, 15);

        // Başlık metni
        doc.setFontSize(18);
        doc.text(t("İZİN LİSTESİ RAPORU"), 70, 15);

        // Header çizgisi
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        // doc.line(10, 25, width - 10, 25);
      }
      
      // Footer arka planı
      doc.setFillColor(245, 245, 245);
      doc.rect(0, height - 15, width, 15, 'F');
      
      // Footer üst çizgisi
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.line(0, height - 15, width, height - 15);
      
      // Sayfa bilgisi
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(t(`Sayfa ${currentPage} / ${pageCount}`), width - 25, height - 5);

      // Telif hakkı
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(t('© Dinox İnsan Kaynakları Yönetim Sistemi'), 15, height - 5);
    },
    margin: { top: 55, bottom: 20, left: 10, right: 10 }
  });
  
  // PDF'i kaydet
  const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
  doc.save(`Izin_Raporu_${currentDate}.pdf`);
};

// Employee exports
export const handleEmployeeExportExcel = (employees: User[]) => {
  const exportData = employees.map(employee => ({
    'ID': employee.id,
    'Ad Soyad': `${employee.firstName} ${employee.lastName}`,
    'Email': employee.email,
    'Telefon': employee.phone || '-',
    'Departman': employee.department?.map(d => d.name).join(', ') || '-',
    'Sube': employee.branch?.map(b => b.name).join(', ') || '-',
    'Rol': employee.role,
    //'Durum': employee.isActive ? 'Aktif' : 'Pasif',
    'Ise Baslama': employee.hireDate ? dayjs(employee.hireDate).format('DD.MM.YYYY') : '-'
  }));

  const wb = utils.book_new();
  const ws = utils.json_to_sheet(exportData);
  utils.book_append_sheet(wb, ws, 'Calisanlar');
  writeFile(wb, `Calisan_Listesi_${dayjs().format('DD.MM.YYYY')}.xlsx`);
};

export const handleEmployeeExportPdf = async (employees: User[]) => {
  // A4 boyutunda yatay (landscape) PDF oluştur
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  // Türkçe karakter desteği için Roboto fontunu yükle
  let pdfFont = 'helvetica';
  try {
    const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
    const customFont = await getFontAsBase64(fontUrl);
    doc.addFileToVFS('Roboto-Regular.ttf', customFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
    pdfFont = 'Roboto';
  } catch (error) {
    console.warn('handleEmployeeExportPdf: Roboto font yüklenemedi, varsayılan font kullanılıyor.', error);
  }
  // Roboto yüklendiyse ham Türkçe metni kullan, yüklenemediyse ASCII'ye normalize et.
  const t = (text: string): string => (pdfFont === 'Roboto' ? text : normalizeTurkishChars(text));

  // Başlık
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(22);
  doc.setTextColor(41, 128, 185);
  doc.text(t('ÇALIŞAN LİSTESİ'), 20, 20);

  // Çizgi ekle
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(20, 25, 280, 25);

  // Tarih ve çalışan sayısı
  doc.setFont(pdfFont, "normal");
  doc.setFontSize(11);
  doc.setTextColor(80, 80, 80);
  doc.text(t(`Oluşturulma Tarihi: ${dayjs().format('DD.MM.YYYY')}`), 20, 35);
  doc.text(t(`Toplam Çalışan: ${employees.length}`), 20, 42);

  // Tablo sütunları
  const tableColumn = [
    ['ID', 'Ad Soyad', 'Email', 'Telefon', 'Departman', 'Şube', 'Rol', 'İşe Başlama'].map(t)
  ];

  // Tablo satırları
  const tableRows = employees.map(employee => [
    employee.id,
    t(`${employee.firstName} ${employee.lastName}`),
    employee.email,
    employee.phone || '-',
    t(employee.department?.map(d => d.name).join(', ') || '-'),
    t(employee.branch?.map(b => b.name).join(', ') || '-'),
    employee.role,
    employee.hireDate ? dayjs(employee.hireDate).format('DD.MM.YYYY') : '-'
  ]);

  // Gelişmiş tablo
  autoTable(doc, {
    head: tableColumn,
    body: tableRows,
    startY: 50,
    theme: 'striped',
    styles: {
      font: pdfFont,
      fontSize: 9,
      cellPadding: 4,
      overflow: 'linebreak',
      lineColor: [80, 80, 80],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      font: pdfFont,
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      cellPadding: 5,
    },
    alternateRowStyles: {
      fillColor: [240, 245, 255],
    },
    columnStyles: {
      0: { cellWidth: 15, halign: 'center' }, // ID
      1: { cellWidth: 40 }, // Ad Soyad
      2: { cellWidth: 50 }, // Email
      3: { cellWidth: 25, halign: 'center' }, // Telefon
      4: { cellWidth: 30 }, // Departman
      5: { cellWidth: 30 }, // Şube
      6: { cellWidth: 25, halign: 'center' }, // Rol
      7: { cellWidth: 25, halign: 'center' }, // İşe Başlama
    },
    didDrawPage: (data) => {
      // Sayfa numarası
      const pageCount = doc.getNumberOfPages();
      const currentPage = data.pageNumber;
      
      // Altbilgi çizgisi
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(15, doc.internal.pageSize.height - 15, doc.internal.pageSize.width - 15, doc.internal.pageSize.height - 15);
      
      // Sayfa numarası
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(
        t(`Sayfa ${currentPage} / ${pageCount}`),
        doc.internal.pageSize.width - 25,
        doc.internal.pageSize.height - 10
      );

      // Alt bilgi
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(t('© Dinox İnsan Kaynakları Yönetim Sistemi'), 15, doc.internal.pageSize.height - 10);
      
      // Üstbilgi (ilk sayfa hariç)
      if (currentPage > 1) {
        doc.setFontSize(12);
        doc.setTextColor(41, 128, 185);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
      }
    }
  });

  doc.save(`Calisan_Listesi_${dayjs().format('DD.MM.YYYY')}.pdf`);
};

/**
 * Herhangi bir veri tipi için genel Excel export fonksiyonu
 * @param data Tabloya dönüştürülecek veri dizisi
 * @param title Excel dosyası için başlık (isteğe bağlı)
 * @param columns Tablo sütun başlıkları ve özellikleri (isteğe bağlı)
 */
export const exportToExcel = <T extends Record<string, any>>(
  data: T[], 
  title: string = 'Rapor', 
  columns?: { header: string; property: keyof T; width?: number }[]
) => {
  // Veriyi Excel formatına dönüştür
  let excelData: Record<string, any>[] = [];
  
  // Eğer sütun bilgileri sağlanmışsa, onları kullan
  if (columns && columns.length > 0) {
    excelData = data.map(item => {
      const row: Record<string, any> = {};
      columns.forEach(col => {
        row[col.header] = item[col.property] !== undefined ? item[col.property] : '';
      });
      return row;
    });
  } else {
    // Değilse, veri nesnesinin tüm anahtarlarını otomatik kullan
    if (data.length > 0) {
      const keys = Object.keys(data[0]);
      excelData = data.map(item => {
        const row: Record<string, any> = {};
        keys.forEach(key => {
          // İlk harfi büyük yap
          const header = key.charAt(0).toUpperCase() + key.slice(1);
          row[header] = item[key] !== undefined ? item[key] : '';
        });
        return row;
      });
    }
  }
  
  // Excel workbook oluşturma
  const workbook = XLSX.utils.book_new();
  
  // Kitap özellikleri ayarlama
  workbook.Props = {
    Title: title,
    Subject: "DINOX Yönetim Sistemi Raporu",
    Author: "DINOX Sistem",
    CreatedDate: new Date()
  };
  
  // Veri yoksa boş bir satır ekle
  if (excelData.length === 0) {
    excelData = [{ "Bilgi": "Bu rapor için veri bulunamadı." }];
  }
  
  // Worksheet oluşturma ve ekleme
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Sütun genişliklerini ayarlama
  const wscols: { wch: number }[] = [];
  
  // Eğer sütun bilgileri sağlanmışsa, genişlikleri kullan
  if (columns && columns.length > 0) {
    columns.forEach(col => {
      wscols.push({ wch: col.width || 15 }); // Genişlik belirtilmemişse 15 kullan
    });
  } else if (excelData.length > 0) {
    // İlk satırın tüm anahtarları için genişlik belirle
    Object.keys(excelData[0]).forEach(key => {
      // Ortalama uzunluğa göre genişlik belirle
      const maxLength = Math.max(
        key.length,
        ...excelData.map(row => String(row[key] || '').length)
      );
      wscols.push({ wch: Math.min(Math.max(10, maxLength + 2), 50) }); // En az 10, en fazla 50 genişlik
    });
  }
  
  worksheet['!cols'] = wscols;
  
  // Çalışma sayfası adı için başlığı düzenle (30 karakterle sınırla)
  const sheetName = title.length > 30 ? title.substring(0, 27) + "..." : title;
  
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  // Excel dosyasını kaydetme
  const fileName = title.replace(/\s+/g, '_').toLowerCase();
  const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
  XLSX.writeFile(workbook, `${fileName}_${currentDate}.xlsx`);
};

/**
 * Türkçe karakterleri destekleyen, temiz görünümlü PDF oluşturma fonksiyonu
 * @param data PDF'e aktarılacak veri dizisi
 * @param title PDF başlığı ve dosya adı
 * @param options PDF özelleştirme seçenekleri
 */
export const exportToPdf = async <T extends Record<string, any>>(
  data: T[],
  title: string,
  options: {
    columns: Array<{
      header: string; // Sütun başlığı
      property: keyof T; // Veri içindeki özellik adı
      width?: number; // Sütun genişliği (opsiyonel)
    }>;
    description?: string; // PDF açıklaması (opsiyonel)
    headerColor?: string; // Üst bilgi rengi (opsiyonel, varsayılan: '#1e40af')
    accentColor?: string; // Vurgu rengi (opsiyonel, varsayılan: '#3b82f6')
    companyName?: string; // Şirket adı (opsiyonel, varsayılan: 'DINOX')
    companyLogo?: string; // Şirket logosu URL (opsiyonel)
    showDate?: boolean; // Tarih gösterme (opsiyonel, varsayılan: true)
    dateFormat?: string; // Tarih formatı (opsiyonel, varsayılan: 'DD.MM.YYYY')
    showPageNumbers?: boolean; // Sayfa numarası gösterme (opsiyonel, varsayılan: true)
    additionalInfo?: Array<{ // Ek bilgiler/istatistikler (opsiyonel)
      label: string;
      value: string;
    }>;
  }
) => {
  // Varsayılan seçenekleri belirle
  const defaultOptions = {
    headerColor: '#1e40af', // Koyu mavi
    accentColor: '#3b82f6', // Mavi
    companyName: 'DINOX',
    showDate: true,
    dateFormat: 'DD.MM.YYYY',
    showPageNumbers: true
  };

  // Kullanıcı seçeneklerini varsayılanlarla birleştir
  const settings = { ...defaultOptions, ...options };

  // Renkleri RGB'ye dönüştür (jsPDF bu formatta kullanıyor)
  const headerColorRGB = hexToRgb(settings.headerColor);
  // const accentColorRGB = hexToRgb(settings.accentColor);

  // A4 boyutunda PDF oluştur
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  // Türkçe karakter desteği için Roboto fontunu yükle
  let pdfFont = 'helvetica';
  try {
    const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
    const customFont = await getFontAsBase64(fontUrl);
    doc.addFileToVFS('Roboto-Regular.ttf', customFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
    pdfFont = 'Roboto';
  } catch (error) {
    console.warn('exportToPdf: Roboto font yüklenemedi, varsayılan font kullanılıyor.', error);
  }
  // Roboto yüklendiyse ham Türkçe metni kullan, yüklenemediyse ASCII'ye normalize et.
  const t = (text: string): string => (pdfFont === 'Roboto' ? text : normalizeTurkishChars(text));

  // PDF başlık ve metadata ayarları - Türkçe karakter desteği
  doc.setLanguage("tr");
  doc.setProperties({
    title: t(title),
    subject: t("DINOX Yönetim Sistemi Raporu"),
    author: settings.companyName,
    keywords: t(`rapor, ${title.toLowerCase()}, pdf`),
    creator: settings.companyName
  });

  // Sayfa boyutları
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();

  // ===== HEADER (ÜST BİLGİ) =====
  // Header arka planı
  doc.setFillColor(headerColorRGB.r, headerColorRGB.g, headerColorRGB.b);
  doc.rect(0, 0, width, 25, 'F');

  // Logo veya Şirket Adı
  if (settings.companyLogo) {
    try {
      // Logo resmi
      doc.addImage(settings.companyLogo, 'PNG', 10, 5, 40, 15);
    } catch (error) {
      // Logo yüklenemezse şirket adını göster
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(t(settings.companyName), 20, 15);
    }
  } else {
    // Logo yerine şirket adı
    doc.setFillColor(Math.max(0, headerColorRGB.r - 30), Math.max(0, headerColorRGB.g - 30), Math.max(0, headerColorRGB.b - 30));
    doc.roundedRect(10, 5, 45, 15, 3, 3, 'F');

    doc.setFont(pdfFont, "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text(t(settings.companyName), 20, 15);
  }

  // Başlık metni (ortada)
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);

  // Başlığı sayfanın ortasına yerleştir
  const titleText = t(title).toLocaleUpperCase('tr-TR');
  const titleWidth = doc.getTextWidth(titleText);
  const titleXPos = (width - titleWidth) / 2;
  doc.text(titleText, titleXPos, 15);

  // Oluşturulma tarihi (sağ üst)
  if (settings.showDate) {
    doc.setFont(pdfFont, "normal");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    const dateText = `${dayjs().format(settings.dateFormat)}`;
    doc.text(dateText, width - 15 - doc.getTextWidth(dateText), 15);
  }

  // ===== AÇIKLAMA BÖLÜMÜ =====
  let currentY = 30; // Header'dan sonraki başlangıç Y pozisyonu

  if (settings.description) {
    doc.setFont(pdfFont, "normal");
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);

    // İki satırlık bir açıklama alanı oluştur
    const splitDescription = doc.splitTextToSize(t(settings.description), width - 30);
    doc.text(splitDescription, 15, currentY);
    
    // Açıklama satır sayısına göre Y pozisyonunu güncelle
    currentY += splitDescription.length * 6 + 5;
  }

  // ===== EK BİLGİLER BÖLÜMÜ (İSTATİSTİKLER) =====
  if (settings.additionalInfo && settings.additionalInfo.length > 0) {
    // İstatistik kutuları
    const infoCount = settings.additionalInfo.length;
    const boxWidth = Math.min(60, (width - 30) / infoCount - 5);
    const boxHeight = 18;
    const boxMargin = 5;
    let startX = 15;
    
    // İstatistik kutuları için arka plan renkleri
    const colors = [
      { bg: lightenColor(settings.headerColor, 0.8), text: settings.headerColor },
      { bg: lightenColor(settings.headerColor, 0.7), text: settings.headerColor },
      { bg: lightenColor(settings.headerColor, 0.6), text: settings.headerColor },
      { bg: lightenColor(settings.headerColor, 0.5), text: settings.headerColor },
    ];
    
    settings.additionalInfo.forEach((info, index) => {
      const colorIndex = index % colors.length;
      const colorRGB = hexToRgb(colors[colorIndex].bg);
      const textColorRGB = hexToRgb(colors[colorIndex].text);
      
      // İstatistik kutusu
      doc.setFillColor(colorRGB.r, colorRGB.g, colorRGB.b);
      doc.roundedRect(startX, currentY, boxWidth, boxHeight, 2, 2, 'F');
      
      // İstatistik değeri (büyük yazı)
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(14);
      doc.setTextColor(textColorRGB.r, textColorRGB.g, textColorRGB.b);

      // Değeri kutunun ortasına hizala
      const infoValueText = t(info.value);
      const valueWidth = doc.getTextWidth(infoValueText);
      doc.text(infoValueText, startX + (boxWidth - valueWidth) / 2, currentY + 10);

      // İstatistik etiketi (küçük yazı)
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(textColorRGB.r, textColorRGB.g, textColorRGB.b);

      // Etiketi kutunun altına hizala
      const infoLabelText = t(info.label);
      const labelWidth = doc.getTextWidth(infoLabelText);
      doc.text(infoLabelText, startX + (boxWidth - labelWidth) / 2, currentY + 16);
      
      // Sonraki kutu için X pozisyonunu güncelle
      startX += boxWidth + boxMargin;
    });
    
    // Y pozisyonunu güncelle
    currentY += boxHeight + 10;
  }

  // ===== TABLO BÖLÜMÜ =====
  // Tablonun sütun başlıkları
  const tableHeaders = settings.columns.map(col => t(col.header));

  // Tablo verileri
  const tableData = data.map(item =>
    settings.columns.map(col => {
      const value = item[col.property];
      return value !== undefined && value !== null
        ? t(String(value))
        : '-';
    })
  );
  
  // Sütun genişlikleri
  const columnStyles: Record<number, { cellWidth?: number, halign?: 'left' | 'center' | 'right' }> = {};
  settings.columns.forEach((col, index) => {
    if (col.width) {
      columnStyles[index] = { cellWidth: col.width };
    }
  });

  // Tablo başlık rengi
  const tableHeaderColor = headerColorRGB;
  
  // Alternatif satır rengi (hafif renkli)
  const alternateRowColor = hexToRgb(lightenColor(settings.headerColor, 0.95));
  
  // Gelişmiş tablo oluşturma
  autoTable(doc, {
    head: [tableHeaders],
    body: tableData,
    startY: currentY,
    theme: 'grid',
    styles: {
      font: pdfFont,
      fontSize: 10,
      cellPadding: 6,
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      textColor: [50, 50, 50],
      valign: 'middle'
    },
    headStyles: {
      fillColor: [tableHeaderColor.r, tableHeaderColor.g, tableHeaderColor.b],
      textColor: 255,
      fontSize: 11,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      minCellHeight: 14
    },
    alternateRowStyles: {
      fillColor: [alternateRowColor.r, alternateRowColor.g, alternateRowColor.b]
    },
    columnStyles: columnStyles,
    // Her sayfada header ve footer
    didDrawPage: (data) => {
      // Sayfa bilgisi
      const pageCount = doc.getNumberOfPages();
      const currentPage = data.pageNumber;
      
      // Sonraki sayfalarda (ilk sayfa hariç) header tekrarı
      if (currentPage > 1) {
        // Header
        doc.setFillColor(headerColorRGB.r, headerColorRGB.g, headerColorRGB.b);
        doc.rect(0, 0, width, 25, 'F');
        
        // Logo veya Şirket Adı
        if (settings.companyLogo) {
          try {
            doc.addImage(settings.companyLogo, 'PNG', 10, 5, 40, 15);
          } catch {
            doc.setFont(pdfFont, "bold");
            doc.setFontSize(16);
            doc.setTextColor(255, 255, 255);
            doc.text(t(settings.companyName), 20, 15);
          }
        } else {
          doc.setFillColor(Math.max(0, headerColorRGB.r - 30), Math.max(0, headerColorRGB.g - 30), Math.max(0, headerColorRGB.b - 30));
          doc.roundedRect(10, 5, 45, 15, 3, 3, 'F');

          doc.setFont(pdfFont, "bold");
          doc.setFontSize(16);
          doc.setTextColor(255, 255, 255);
          doc.text(t(settings.companyName), 20, 15);
        }

        // Başlık
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(18);
        doc.text(titleText, titleXPos, 15);

        // Tarih
        if (settings.showDate) {
          doc.setFont(pdfFont, "normal");
          doc.setFontSize(10);
          doc.setTextColor(255, 255, 255);
          const dateText = `${dayjs().format(settings.dateFormat)}`;
          doc.text(dateText, width - 15 - doc.getTextWidth(dateText), 15);
        }
      }

      if (settings.showPageNumbers) {
        // Footer arka planı
        doc.setFillColor(245, 245, 245);
        doc.rect(0, height - 15, width, 15, 'F');

        // Footer üst çizgisi
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(0, height - 15, width, height - 15);

        // Sayfa numarası
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(t(`Sayfa ${currentPage} / ${pageCount}`), width - 25, height - 5);

        // Telif hakkı
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text(t('© Dinox İnsan Kaynakları Yönetim Sistemi'), 15, height - 5);
      }
    },
    margin: { top: 55, bottom: 20, left: 10, right: 10 }
  });
  
  // PDF'i kaydet
  const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
  doc.save(`${title.replace(/\s+/g, '_')}_${currentDate}.pdf`);
};

// Yardımcı fonksiyonlar

// Hex renk kodunu RGB'ye çevirme
function hexToRgb(hex: string): { r: number, g: number, b: number } {
  // # işaretini kaldır
  hex = hex.replace(/^#/, '');
  
  // Hex kodunu RGB'ye dönüştür
  const bigint = parseInt(hex, 16);
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  };
}

// Rengi açma/koyulaştırma
function lightenColor(color: string, amount: number): string {
  const rgb = hexToRgb(color);
  const lighten = (value: number) => Math.min(255, Math.round(value + (255 - value) * amount));
  
  const r = lighten(rgb.r);
  const g = lighten(rgb.g);
  const b = lighten(rgb.b);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Genel kullanım için gelişmiş PDF oluşturma fonksiyonu.
 * Her türlü veri için, Türkçe karakter desteği ve modern tasarım ile PDF oluşturur.
 * 
 * @param data Tabloya dönüştürülecek veri dizisi
 * @param options PDF özelleştirme seçenekleri
 * @returns void - PDF otomatik olarak indirilir
 */
export const createPdf = async (
  data: any[],
  options: {
    title: string;
    description?: string;
    headerColor?: string;
    accentColor?: string;
    columns: { header: string; property: string; width: number; align?: 'left' | 'center' | 'right' }[];
    showStatistics?: boolean;
    companyName?: string;
    statistics?: Array<{ label: string; value: string | number }>;
    footer?: {
      text: string;
      showPageNumbers?: boolean;
    };
  }
) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    putOnlyUsedFonts: true,
    compress: true
  });

  // Türkçe karakter desteği için Roboto fontunu yükle (normal + bold aynı dosya; bold için fallback kullanılmaz, İ/Ş/Ğ doğru çıkar)
  let pdfFont = 'helvetica';
  try {
    const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
    const customFont = await getFontAsBase64(fontUrl);
    doc.addFileToVFS('Roboto-Regular.ttf', customFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
    pdfFont = 'Roboto';
  } catch (error) {
    console.warn('createPdf: Roboto font yüklenemedi, varsayılan font kullanılıyor.', error);
  }

  // Sayfa boyutları ve renkleri hesapla
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const headerColor = options.headerColor || '#2980b9';
  const headerRGB = hexToRgb(headerColor);
  const companyName = options.companyName || 'DINOX';
  
  // Header bölümü
  doc.setFillColor(headerRGB.r, headerRGB.g, headerRGB.b);
  doc.rect(0, 0, width, 40, 'F');
  
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  
  // Başlık (Türkçe İ/ı kuralları: toLocaleUpperCase('tr-TR'))
  const titleText = options.title.toLocaleUpperCase('tr-TR');
  let titleWidth = 0;
  try {
    titleWidth = doc.getTextWidth(titleText);
  } catch (error) {
    titleWidth = titleText.length * 10;
  }
  
  doc.text(titleText, (width - titleWidth) / 2, 25);
  
  if (options.description) {
    doc.setFont(pdfFont, "normal");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(options.description, 15, 35);
  }

  // İstatistikler bölümü - eğer varsa
  let startY = 50;
  if (options.showStatistics && options.statistics && options.statistics.length > 0) {
    // İstatistikler için arka plan
    doc.setFillColor(245, 247, 250);
    doc.rect(10, 45, width - 20, 28, 'F');
    
    // İstatistikler başlığı
    doc.setFont(pdfFont, "bold");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text("ÖZET İSTATİSTİKLER", 15, 54);
    
    // İstatistikleri yan yana göster
    const statCount = options.statistics.length;
    const statWidth = (width - 30) / statCount;
    
    options.statistics.forEach((stat, index) => {
      const xPos = 15 + (index * statWidth);
      
      // İstatistik kutusu (basit dikdörtgen)
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.rect(xPos, 58, statWidth - 5, 12, 'FD');
      
      // İstatistik değeri
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(11);
      doc.setTextColor(headerRGB.r, headerRGB.g, headerRGB.b);
      const valueText = String(stat.value);
      let valueWidth = 0;
      try {
        valueWidth = doc.getTextWidth(valueText);
      } catch (error) {
        valueWidth = valueText.length * 6;
      }
      doc.text(valueText, xPos + (statWidth - 5 - valueWidth) / 2, 65);
      
      // İstatistik etiketi
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      const labelText = String(stat.label);
      let labelWidth = 0;
      try {
        labelWidth = doc.getTextWidth(labelText);
      } catch (error) {
        labelWidth = labelText.length * 4;
      }
      doc.text(labelText, xPos + (statWidth - 5 - labelWidth) / 2, 68);
    });
    
    startY = 80;
  }

  // Veri kontrolü - eğer veri yoksa uyarı göster
  if (!data || data.length === 0) {
    doc.setFont(pdfFont, "normal");
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text("Bu rapor için veri bulunamadı.", 15, startY + 20);
    const filename = `${options.title.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
    doc.save(filename);
    return;
  }

  const head = [options.columns.map((col) => col.header)];
  const body = data.map((row) =>
    options.columns.map(column => {
      const value = row[column.property as keyof typeof row];
      return value !== undefined && value !== null ? String(value) : '-';
    })
  );

  // Sütun stilleri
  const columnStyles: Record<number, { cellWidth?: number, halign?: 'left' | 'center' | 'right' }> = {};
  
  options.columns.forEach((column, index) => {
    columnStyles[index] = {
      cellWidth: column.width,
      halign: column.align || 'left'
    };
  });

  // Tablo oluştur - Roboto ile Türkçe karakter desteği
  autoTable(doc, {
    head: head,
    body: body,
    startY: startY,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: [200, 200, 200],
      lineWidth: 0.3,
      font: pdfFont,
      textColor: [40, 40, 40],
      overflow: 'linebreak',
      cellWidth: 'wrap'
    },
    headStyles: {
      fillColor: [headerRGB.r, headerRGB.g, headerRGB.b],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      font: pdfFont,
      halign: 'center',
      valign: 'middle',
      minCellHeight: 12,
      cellPadding: 4
    },
    bodyStyles: {
      valign: 'middle',
      cellPadding: 2,
      font: pdfFont,
      fontSize: 8
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [250, 250, 252]
    },
    // Her sayfa için üstbilgi ve altbilgi
    didDrawPage: (data) => {
      // Sonraki sayfalar için header (ilk sayfa hariç)
      if (data.pageNumber > 1) {
        // Header arka planı
        doc.setFillColor(headerRGB.r, headerRGB.g, headerRGB.b);
        doc.rect(0, 0, width, 40, 'F');
        
        // Başlık metni (Türkçe İ/ı kuralları)
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        const titleText2 = options.title.toLocaleUpperCase('tr-TR');
        let titleWidth2 = 0;
        try {
          titleWidth2 = doc.getTextWidth(titleText2);
        } catch (error) {
          titleWidth2 = titleText2.length * 9;
        }
        doc.text(titleText2, (width - titleWidth2) / 2, 25);
      }
      
      // Footer
      doc.setFillColor(240, 240, 240);
      doc.rect(0, height - 20, width, 20, 'F');
      
      // Sayfa numarası
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      
      if (options.footer?.showPageNumbers) {
        doc.setFont(pdfFont, "normal");
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        doc.text(
          `Sayfa ${data.pageNumber} / ${doc.getNumberOfPages()}`,
          width - 30,
          height - 8
        );
      }
      
      // Footer bilgisi
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      const footerText = options.footer?.text || `© ${new Date().getFullYear()} ${companyName} - Tüm Hakları Saklıdır`;
      doc.text(footerText, 15, height - 8);
    },
    margin: { top: startY + 5, bottom: 35, left: 8, right: 8 }
  });
  
  // Dosya adını belirle
  const filename = `${options.title.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
  
  // PDF'i kaydet
  doc.save(filename);
};

/**
 * Puantaj ve Maaş raporları için özel PDF oluşturma fonksiyonu
 * Bu fonksiyon sadece puantaj ve maaş raporları için kullanılır
 * Diğer PDF'ler etkilenmez
 */
export const createTimesheetPayrollPdf = async (
  data: any[],
  options: {
    title: string;
    description?: string;
    headerColor?: string;
    columns: { header: string; property: string; width: number; align?: 'left' | 'center' | 'right' }[];
    showStatistics?: boolean;
    statistics?: Array<{ label: string; value: string | number }>;
    footer?: {
      text: string;
      showPageNumbers?: boolean;
    };
  }
) => {
  const doc = new jsPDF("landscape"); // Yatay (landscape) mod - tüm sütunlar için yer açar

  // Türkçe karakter desteği için Roboto fontunu yükle
  let pdfFont = 'helvetica';
  try {
    const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
    const customFont = await getFontAsBase64(fontUrl);
    doc.addFileToVFS('Roboto-Regular.ttf', customFont);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
    pdfFont = 'Roboto';
  } catch (error) {
    console.warn('createTimesheetPayrollPdf: Roboto font yüklenemedi, varsayılan font kullanılıyor.', error);
  }

  // Roboto başarıyla yüklendiyse Türkçe karakterler zaten doğru basılır — sadece
  // font yüklemesi başarısız olup Helvetica'da kaldığımızda ASCII'ye normalize et.
  const t = (text: string): string => (pdfFont === 'Roboto' ? text : normalizeTurkishChars(text));

  // Sayfa boyutları ve renkleri hesapla
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const headerColor = options.headerColor || '#1890ff';
  const headerRGB = hexToRgb(headerColor);

  // Veri kontrolü
  if (!data || data.length === 0) {
    doc.setFont(pdfFont, "normal");
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(t("Bu rapor için veri bulunamadı."), 15, 50);
    const filename = `${options.title.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
    doc.save(filename);
    return;
  }

  // Header bölümü - küçük ve kompakt
  doc.setFillColor(headerRGB.r, headerRGB.g, headerRGB.b);
  doc.rect(0, 0, width, 30, 'F'); // Daha küçük header (30px)
  
  doc.setFont(pdfFont, "bold");
  doc.setFontSize(16); // Daha küçük font
  doc.setTextColor(255, 255, 255);
  
  // Başlık genişliğini hesapla - Türkçe karakter desteği ile
  const titleText = t(options.title).toLocaleUpperCase('tr-TR');
  let titleWidth = 0;
  try {
    titleWidth = doc.getTextWidth(titleText);
  } catch (error) {
    titleWidth = titleText.length * 9;
  }
  
  doc.text(titleText, (width - titleWidth) / 2, 18);
  
  if (options.description) {
    doc.setFont(pdfFont, "normal");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(t(options.description), 15, 27);
  }

  // İstatistikler bölümü - eğer varsa (daha kompakt)
  let startY = 40;
  if (options.showStatistics && options.statistics && options.statistics.length > 0) {
    // İstatistikler için arka plan - daha küçük
    doc.setFillColor(245, 247, 250);
    doc.rect(10, 35, width - 20, 18, 'F');
    
    // İstatistikleri yan yana göster
    const statCount = options.statistics.length;
    const statWidth = (width - 30) / statCount;
    
    options.statistics.forEach((stat, index) => {
      const xPos = 15 + (index * statWidth);
      
      // İstatistik kutusu
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.rect(xPos, 38, statWidth - 5, 12, 'FD');
      
      // İstatistik değeri
      doc.setFont(pdfFont, "bold");
      doc.setFontSize(10);
      doc.setTextColor(headerRGB.r, headerRGB.g, headerRGB.b);
      const valueText = t(String(stat.value));
      let valueWidth = 0;
      try {
        valueWidth = doc.getTextWidth(valueText);
      } catch (error) {
        valueWidth = valueText.length * 5;
      }
      doc.text(valueText, xPos + (statWidth - 5 - valueWidth) / 2, 46);
      
      // İstatistik etiketi
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      const labelText = t(String(stat.label));
      let labelWidth = 0;
      try {
        labelWidth = doc.getTextWidth(labelText);
      } catch (error) {
        labelWidth = labelText.length * 3.5;
      }
      doc.text(labelText, xPos + (statWidth - 5 - labelWidth) / 2, 50);
    });
    
    startY = 58;
  }

  // Tablo başlıkları ve verileri - Türkçe karakter desteği ile
  const head = [options.columns.map((col) => t(col.header))];
  
  // Veri mapping - property'ye direkt erişim
  const body = data.map((row) => {
    const rowData = options.columns.map((column) => {
      // Property'ye direkt erişim
      const value = row[column.property];
      return value !== undefined && value !== null ? t(String(value)) : '-';
    });
    return rowData;
  });

  // Sütun stilleri - genişlikleri dinamik hesapla
  const columnStyles: Record<number, { cellWidth?: number, halign?: 'left' | 'center' | 'right' }> = {};
  
  // Toplam genişlik hesapla (sayfa genişliği - margin'ler)
  const availableWidth = width - 20; // 10px sol + 10px sağ margin = 20px
  
  // Toplam genişlik değerlerini hesapla
  const totalWidth = options.columns.reduce((sum, col) => sum + (col.width || 0), 0);
  
  options.columns.forEach((column, index) => {
    // Genişlikleri sayfa genişliğine göre orantılı olarak hesapla
    const cellWidth = totalWidth > 0 
      ? (column.width / totalWidth) * availableWidth 
      : availableWidth / options.columns.length;
    
    columnStyles[index] = {
      cellWidth: cellWidth,
      halign: column.align || 'left'
    };
  });

  // Tablo oluştur - tam genişlik, kompakt tasarım
  autoTable(doc, {
    head: head,
    body: body,
    startY: startY,
    theme: 'striped', // Basit çizgili tema
    styles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: [200, 200, 200],
      lineWidth: 0.5,
      font: pdfFont,
      textColor: [0, 0, 0],
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [headerRGB.r, headerRGB.g, headerRGB.b],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold',
      font: pdfFont,
      halign: 'center',
      valign: 'middle',
      cellPadding: 4
    },
    bodyStyles: {
      valign: 'middle',
      cellPadding: 2,
      font: pdfFont,
      fontSize: 8
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    // Tablo genişliğini maksimize et - sayfa genişliğine ayarla
    tableWidth: 'auto',
    // Her sayfa için üstbilgi ve altbilgi
    didDrawPage: (data) => {
      // Sonraki sayfalar için header
      if (data.pageNumber > 1) {
        doc.setFillColor(headerRGB.r, headerRGB.g, headerRGB.b);
        doc.rect(0, 0, width, 30, 'F');
        
        doc.setFont(pdfFont, "bold");
        doc.setFontSize(14);
        doc.setTextColor(255, 255, 255);
        const titleText2 = t(options.title).toLocaleUpperCase('tr-TR');
        let titleWidth2 = 0;
        try {
          titleWidth2 = doc.getTextWidth(titleText2);
        } catch (error) {
          titleWidth2 = titleText2.length * 8;
        }
        doc.text(titleText2, (width - titleWidth2) / 2, 18);
      }
      
      // Footer - küçük
      doc.setFillColor(240, 240, 240);
      doc.rect(0, height - 20, width, 20, 'F');
      
      // Sayfa numarası
      doc.setFont(pdfFont, "normal");
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      
      if (options.footer?.showPageNumbers) {
        doc.text(
          t(`Sayfa ${data.pageNumber} / ${doc.getNumberOfPages()}`),
          width - 30,
          height - 8
        );
      }
      
      // Footer bilgisi
      const footerText = t(options.footer?.text || `© ${new Date().getFullYear()} DINOX - Tüm Hakları Saklıdır`);
      doc.text(footerText, 15, height - 8);
    },
    // Margin'leri ayarla
    margin: { top: startY + 5, bottom: 25, left: 10, right: 10 }
  });
  
  // Dosya adını belirle
  const filename = `${options.title.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
  
  // PDF'i kaydet
  doc.save(filename);
};
export const getFontAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch font: ${response.statusText}`);
  }
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URI prefix e.g., "data:font/ttf;base64,"
        resolve(reader.result.split(',')[1]);
      } else {
        reject(new Error('Failed to read font as base64.'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const translateLeaveType = (type: string): string => {
  const translations: { [key: string]: string } = {
    sick: "Hastalık İzni",
    unpaid: "Ücretsiz İzin",
    casual: "Günlük İzin",
    hourly: "Saatlik İzin",
    annual: "Yıllık İzin",
    medical: "Sağlık İzni",
    personal: "Kişisel İzin",
    marriage: "Evlilik İzni",
    educational: "Eğitim İzni",
    birth: "Doğum İzni",
    special: "Özel İzin",
    paternity: "Babalık İzni",
    death: "Ölüm İzni"
  };
  return translations[type] || type;
};


export const exportLeaveDetailsToPdf = async (leave: LeaveDto, companyName: string) => {
    const doc = new jsPDF();

    let fontLoaded = false;
    try {
        // Fetch and add Roboto font for UTF-8 support from a reliable CDN
        const fontUrl = 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf';
        const customFont = await getFontAsBase64(fontUrl);

        doc.addFileToVFS('Roboto-Regular.ttf', customFont);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.setFont('Roboto');
        fontLoaded = true;
    } catch (error) {
        console.error("Font could not be loaded, using default font. Turkish characters may not be rendered correctly.", error);
        // If font loading fails, we proceed with the default font.
    }

    // Roboto yüklendiyse Türkçe karakterler zaten doğru basılır — sadece font
    // yüklemesi başarısız olduğunda ASCII'ye normalize ederek en azından okunabilir kal.
    const t = (text: string): string => (fontLoaded ? text : normalizeTurkishChars(text));
    const autoTableFont = fontLoaded ? 'Roboto' : 'helvetica';

    const pageHeight = doc.internal.pageSize.height;
    let y = 15; // Vertical position start
    let leaveBalanceByType: Record<string, { typeName?: string; used?: number; remaining?: number; unit?: string }> = {};

    // Company Name (instead of logo)
    doc.setFontSize(16);
    doc.text(t(companyName || "Şirket Adı Belirtilmemiş"), 14, y);
    y += 15;

    // Title
    doc.setFontSize(20);
    doc.text(t('İzin Talep Formu'), 105, y, { align: 'center' });
    y += 15;

    // Employee Information
    doc.setFontSize(14);
    doc.text(t('Çalışan Bilgileri'), 14, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(t(`Adı Soyadı: ${leave.user?.firstName || ''} ${leave.user?.lastName || ''}`), 14, y);
    doc.text(t(`Departman: ${leave.user?.department?.map(d => d.name).join(', ') || 'N/A'}`), 105, y);
    y += 7;
    doc.text(t(`T.C. Kimlik No: ${leave.user?.nationalId || '-'}`), 14, y);
    y += 10;

    // Leave Information
    doc.setFontSize(14);
    doc.text(t('İzin Bilgileri'), 14, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(t(`İzin Türü: ${translateLeaveType(leave.type)}`), 14, y);
    doc.text(t(`Tarih Aralığı: ${dayjs(leave.startDate).format('DD/MM/YYYY')} - ${dayjs(leave.endDate).format('DD/MM/YYYY')}`), 105, y);
    y += 7;
    const totalDays = dayjs(leave.endDate).diff(dayjs(leave.startDate), 'day') + 1;
    doc.text(t(`Toplam Süre: ${totalDays} gün`), 105, y);
    y += 7;
    doc.text(t(`Talep Tarihi: ${dayjs(leave.createdAt).format('DD/MM/YYYY HH:mm')}`), 14, y);
    y += 7;
    doc.text(t(`İzin Sebebi: ${leave.reason}`), 14, y);
    y += 10;

    // İzin bakiyesi (tüm izin türleri) - PDF altında göster
    if (leave.user?.id) {
      try {
        const balanceResponse = await leaveService.getComprehensiveLeaveBalance(leave.user.id);
        leaveBalanceByType =
          balanceResponse?.leaveBalance ||
          balanceResponse?.data?.leaveBalance ||
          {};
      } catch (error) {
        console.warn('İzin bakiyesi PDF için alınamadı:', error);
      }
    }

    const balanceEntries = Object.entries(leaveBalanceByType || {});
    if (balanceEntries.length > 0) {
      doc.setFontSize(14);
      doc.text(t('İzin Hakları Özeti'), 14, y);
      y += 8;

      const balanceHead = [['İzin Türü', 'Kullanılan', 'Kalan'].map(t)];
      const balanceBody = balanceEntries.map(([type, value]) => [
        t(value?.typeName || translateLeaveType(type)),
        t(`${value?.used ?? 0} ${value?.unit || 'gün'}`),
        t(`${value?.remaining ?? 0} ${value?.unit || 'gün'}`)
      ]);

      (doc as any).autoTable({
        startY: y,
        head: balanceHead,
        body: balanceBody,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], font: autoTableFont },
        styles: { font: autoTableFont, fontSize: 9 },
      });

      y = ((doc as any).lastAutoTable?.finalY || y) + 10;
    }

    // Durum (her zaman göster)
    const getLeaveStatusLabel = (): string => {
        switch (leave.status) {
            case LeaveStatus.APPROVED: return 'Onaylandı';
            case LeaveStatus.REJECTED: return 'Reddedildi';
            case LeaveStatus.PENDING_HIERARCHY_APPROVAL: return 'Onay Bekliyor';
            case LeaveStatus.PENDING:
            default: return 'Beklemede';
        }
    };
    doc.setFontSize(10);
    doc.text(t(`Durum: ${getLeaveStatusLabel()}`), 14, y);
    y += 15;

    const head = [['Adım', 'Onaycı', 'Karar', 'Tarih', 'Not'].map(t)];
    let body: (string | number)[][] = [];

    if (leave.approvalFlow?.steps?.length) {
        leave.approvalFlow.steps.forEach(step => {
            const karar = step.status === 'approved' ? 'Onaylandı' : step.status === 'rejected' ? 'Reddedildi' : 'Beklemede';
            body.push([
                step.stepOrder,
                t(`${step.approver.firstName} ${step.approver.lastName}`),
                t(karar),
                step.decisionDate ? dayjs(step.decisionDate).format('DD/MM/YYYY HH:mm') : '-',
                '-'
            ]);
        });
    } else if (leave.approvalHistory?.length) {
        body = leave.approvalHistory.map(h => [
            h.step,
            t(h.approverName),
            t(h.decision === 'approved' ? 'Onaylandı' : 'Reddedildi'),
            dayjs(h.decisionDate).format('DD/MM/YYYY HH:mm'),
            t(h.notes || '-')
        ]);
    }

    if (body.length > 0) {
        doc.setFontSize(14);
        doc.text(t('Onay Süreci Geçmişi'), 14, y);
        y += 8;
        (doc as any).autoTable({
            startY: y,
            head: head,
            body: body,
            theme: 'grid',
            headStyles: { fillColor: [75, 75, 75], font: autoTableFont },
            styles: { font: autoTableFont },
        });
    }

    doc.save(`izin-formu-${leave.user?.firstName}-${leave.user?.lastName}.pdf`);
};